// Per-KTS-doodad-type registry. Replaces the 4-bucket regex routing that
// world.ts used to do — every common doodad type gets a hand-mapped variant
// pool here. Tail types (rare, < ~5 instances) and unmapped types fall through
// to the per-shape bucket fallback in world.ts.
//
// Spec: docs/superpowers/specs/2026-04-29-real-meshes-pass-design.md
// Pattern: docs/build/data-driven-features.md (single source of truth)
//
// Lives in shared/src/ (not client/src/) so server-side QA tests can import
// without crossing workspace boundaries. NatureKey is also defined here for
// the same reason — the client's asset loader re-exports from this file.
//
// Variant selection is deterministic: hash(objectId) % variants.length. Same
// node renders the same mesh across reloads / clients / dev-server restarts.

// Identifies one of the loaded glTF assets in `client/public/assets/nature/`.
// Adding a new asset = add a new key here AND a path in `client/src/assets.ts`.
export type NatureKey =
  // — Phase 1 coverage —
  | "commonTree5"
  | "pine3"
  | "rockMedium3"
  | "pebbleRound3"
  | "bushCommon"
  | "grassTall"
  | "mushroomCommon"
  // — Phase 2 variety: trees —
  | "commonTree1" | "commonTree2" | "commonTree3" | "commonTree4"
  | "pine1" | "pine2" | "pine4" | "pine5"
  | "twistedTree1" | "twistedTree2" | "twistedTree3" | "twistedTree4" | "twistedTree5"
  | "deadTree1" | "deadTree2" | "deadTree3" | "deadTree4" | "deadTree5"
  // — Phase 2 variety: rocks —
  | "rockMedium1" | "rockMedium2"
  | "pebbleRound1" | "pebbleRound2" | "pebbleRound4" | "pebbleRound5"
  | "pebbleSquare3" | "pebbleSquare4"
  // — Phase 2 variety: foliage + flowers —
  | "bushCommonFlowers" | "fern1"
  | "flower3Single" | "flower3Group" | "flower4Single" | "flower4Group"
  | "grassCommonShort" | "mushroomLaetiporus"
  // — Stone-age re-theme: EXPLORER trees + rocks (baked Unity→glTF, see
  //   game/tools/asset-export). Harvestable trees are the GREEN family (Tree_1x);
  //   Tree_8D is the giant elder. Rocks: A-suffix = grey stone.
  | "exTree1A" | "exTree1B" | "exTree1E" | "exTree8D"
  | "exRockM1A" | "exRockM2A" | "exRockM3A"
  // — decorative trees (non-green warm/autumn families) + bare-shape grove set —
  | "exTree2B" | "exTree2D" | "exTree2F" | "exTree3A" | "exTree3C" | "exTree3E"
  | "exTree4B" | "exTree4C" | "exTree5B" | "exTree5C" | "exTree6B" | "exTree6C"
  | "exTree7B" | "exTree7C" | "exTree8B" | "exTree8C" | "exTree8E"
  | "exTree5A" | "exTree7A" | "exTree8A"
  | "exLog1A" | "exLog2A" | "exTrunk1A"
  // — decorative rocks (A=grey, B=brown) + small/large variety —
  | "exRockL1A" | "exRockL2A" | "exRockL3A" | "exRockL1B"
  | "exRockM1B" | "exRockM2B" | "exRockM3C"
  | "exRockS1A" | "exRockS1B" | "exRockS1C" | "exRockS1D"
  // — scatter detail: mushrooms, ferns, bones/carcass —
  | "exMush1A" | "exMush2A" | "exMush3A" | "exMush4A" | "exMush5A"
  | "exFern1A" | "exFern2A"
  | "exBone1A" | "exBone2A" | "exCarcass1A" | "exCarcass2A" | "exTusk1A"
  // — canyon framing —
  | "exCanyonW1A" | "exCanyonW2A" | "exCanyonW3A"
  | "exCliff1A" | "exCliff2A" | "exCliff5A";

export type DoodadEntry = {
  // 1+ NatureKey variants. Hashed objectId picks one per instance.
  variants: NatureKey[];
  // Single scalar applied to every variant. Multi-variant rows where one
  // variant needs a different scale are split into two rows.
  scale: number;
  // Optional row-wide tint (hex 0xRRGGBB). Used for biome shifts (Char* =
  // scorched warm-brown, Zerus* = alien purple, Agria crops = wheat-gold).
  tint?: number;
  // Optional per-instance tint pool. Hash picks one tint per object, breaking
  // green-monotone tree canopies with accent autumn colors. `0` is a sentinel
  // meaning "no tint" (use the base material). Repeat `0` to weight the pool
  // toward the untinted majority. Overrides `tint` when set.
  tintPool?: number[];
  // Optional self-glow. Used for lava/glowing-rock biomes.
  emissive?: number;
  emissiveIntensity?: number;
  // Optional Y-rotation offset in radians for source-axis mismatches.
  rotOffset?: number;
};

// "skip" flags doodad types that exist on the KTS map but should NOT render
// (logical pathing blockers, lighting markers, weather props). Treated
// distinctly from registry misses in the coverage assertion.
export type DoodadRegistryRow = DoodadEntry | "skip";

// Top-25 mapping (covers ~95% of map instances). Each row's variants come
// from the EXPLORER stone-age baked glTF set (game/tools/asset-export/baked_set.json).
// Tree scales target 5–9m rendered height; native bbox heights are in baked_set.json.
// Rocks: A-suffix = grey (dominant), B-suffix = brown accent.
export const DOODAD_REGISTRY: Record<string, DoodadRegistryRow> = {
  // — top 25 by count —
  // Warm/autumn decorative trees — mix of mid-height families (exTree2B/2D ≈11m native→0.6,
  // exTree3A/3C ≈8.4m native→0.75, exTree6B/6C ≈12m native→0.6). No tintPool needed —
  // meshes are already warm-coloured.
  HavenTree: {
    variants: ["exTree2B", "exTree2D", "exTree3A", "exTree3C", "exTree6B", "exTree6C"],
    scale: 0.65,
  },
  // Tall canopy trees (exTree4B/4C ≈14m native→0.5, exTree5B/5C ≈14m native→0.5).
  BelShirCurtain:      { variants: ["exTree4B", "exTree4C", "exTree5B", "exTree5C"], scale: 0.50 },
  // Gnarled open-canopy trees (exTree8B ≈17m native→0.45, exTree8C ≈22m native→0.35).
  AiurTree:            { variants: ["exTree8B", "exTree8C", "exTree7C"], scale: 0.45 },
  MarSaraGroundProps:  { variants: ["grassTall", "grassCommonShort", "pebbleRound2", "mushroomCommon"], scale: 0.4 },
  // Large rocks: A=grey dominant, sprinkle B=brown.
  Rock:                { variants: ["exRockL1A", "exRockL2A", "exRockL3A", "exRockL1B"], scale: 1.0 },
  HavenUnderbrush:     { variants: ["bushCommon", "bushCommonFlowers", "fern1"], scale: 0.55 },
  RockLarge:           { variants: ["exRockL1A", "exRockL2A", "exRockL3A", "exRockL1B"], scale: 1.1 },
  // Dead canopy trees — mix of large families (exTree4B/5B ≈14m→0.5, exTree8B ≈17m→0.45).
  MarSaraTreeDead:     { variants: ["exTree4B", "exTree5B", "exTree8B"], scale: 0.50 },
  // Stone-age bones + carcasses replacing plain pebble stand-ins.
  MarSaraBones:        { variants: ["exBone1A", "exBone2A", "exCarcass1A"], scale: 1.0 },
  // Medium rocks for chiseled look.
  ChiseledRock:        { variants: ["exRockM1A"], scale: 1.2 },
  Vines:               { variants: ["bushCommon"], scale: 0.45 },
  AgriaUnderbrush:     { variants: ["bushCommonFlowers", "fern1"], scale: 0.55 },
  // Medium rocks: A=grey, B=brown accent.
  NewFolsomRockMedium: { variants: ["exRockM1A", "exRockM2A", "exRockM3A", "exRockM1B"], scale: 1.0 },
  RubbleWall90:        { variants: ["exRockS1A", "exRockS1B", "exRockS1C", "exRockS1D"], scale: 1.2 },
  // Charred rock: scorched warm-brown tint, grey base mesh still reads through.
  CharRock:            { variants: ["exRockM1A", "exRockM2A", "exRockM1B"], scale: 1.0, tint: 0x4a3a32 },
  // Bare/twisted open-canopy trees (exTree7B ≈8m native→0.85, exTree7C ≈9m→0.75).
  BelShirTree:         { variants: ["exTree7B", "exTree7C", "exTree8B"], scale: 0.75 },
  CharRockLarge:       { variants: ["exRockL1A", "exRockL2A", "exRockL1B"], scale: 1.1, tint: 0x4a3a32 },
  // Small-medium trees (exTree3E ≈5m native→1.2, exTree2F ≈4.3m→1.4, exTree8E ≈5m→1.3).
  MarSaraTree:         { variants: ["exTree3E", "exTree2F", "exTree8E"], scale: 1.3 },
  Flower:              { variants: ["flower3Single", "flower4Single", "flower3Group", "flower4Group"], scale: 0.5 },
  RubbleWallCorner:    { variants: ["exRockS1A", "exRockS1B", "exRockS1C"], scale: 1.2 },
  AgriaCrops:          { variants: ["bushCommonFlowers"], scale: 0.55, tint: 0xc8a64d },
  // Bare/dead trees for charred dune look (exTree5A ≈10m→0.7, exTree7A ≈5.5m→1.0).
  CharDunesCurtain:    { variants: ["exTree5A", "exTree7A"], scale: 0.80 },
  // Small rocks for scatter.
  RockSmall:           { variants: ["exRockS1A", "exRockS1B", "exRockS1C", "exRockS1D"], scale: 1.2 },
  TyphonRock:          { variants: ["exRockL1A", "exRockL2A", "exRockL3A"], scale: 1.0 },
  PlantsJungle:        { variants: ["fern1", "bushCommon"], scale: 0.6 },

  // — tail (rare types with visible meshes) —
  CliffSpires:         { variants: ["exRockL3A"], scale: 1.8 },
  AiurFoliage:         { variants: ["bushCommon"], scale: 0.5 },
  HavenFoliage:        { variants: ["bushCommon"], scale: 0.5 },
  AgriaFoliage:        { variants: ["bushCommonFlowers"], scale: 0.5 },
  ZerusGroundVines:    { variants: ["bushCommon"], scale: 0.4 },
  RockGiant:           { variants: ["exRockL1A", "exRockL2A", "exRockL3A"], scale: 1.5 },
  PlantsAlien:         { variants: ["twistedTree1"], scale: 0.5 },
  // Stone-age bones for charred biome.
  CharBones:           { variants: ["exBone1A", "exBone2A"], scale: 1.0 },
  // Large canopy tree (exTree4C ≈15m native→0.55 → ~8m).
  ZerusLargeTree:      { variants: ["exTree4C"], scale: 0.55 },
  CharFlowerSmall:     { variants: ["flower4Single"], scale: 0.5, tint: 0x6a4828 },
  RockWaterfall:       { variants: ["exRockL1A", "exRockL2A"], scale: 1.1 },
  ZerusBrambles:       { variants: ["bushCommonFlowers"], scale: 0.5, tint: 0x4a2a3a },
  Bush:                { variants: ["bushCommon"], scale: 0.6 },
  RockFormationLarge:  { variants: ["exRockL2A", "exRockL3A"], scale: 1.3 },
  Void_CliffRocks_Rubble: { variants: ["exRockS1A", "exRockS1B", "exRockS1D"], scale: 1.0 },
  ZergSpire:           { variants: ["twistedTree2"], scale: 1.2, tint: 0x6a3a4a },
  ZergSpireLarge:      { variants: ["twistedTree2"], scale: 2.0, tint: 0x6a3a4a },
  TempleBricks:        { variants: ["exRockM1A", "exRockM3A"], scale: 0.9 },
  // Bone relics + tusks for CharRibs.
  CharRibs:            { variants: ["exBone1A", "exCarcass2A", "exTusk1A"], scale: 0.9 },
  ZerusLavaGroundSpire: { variants: ["exRockL1A"], scale: 1.2, tint: 0x6a3025 },
  // LOS-blocking tree — medium height (exTree3A ≈8.4m→0.75).
  TreeHavenNoLOS:      { variants: ["exTree3A", "exTree6B"], scale: 0.75 },
  BelShirFoliage:      { variants: ["bushCommon"], scale: 0.5 },
  RockVolcanicGiant:   { variants: ["exRockL1A", "exRockL3A"], scale: 1.5, tint: 0x4a2520 },
  // Mid-canopy single-species decorative (exTree7B ≈8m native→0.85).
  AgriaTree:           { variants: ["exTree7B", "exTree7C"], scale: 0.85 },
  ShearedRocksDesert:  { variants: ["exRockM1A", "exRockM2A"], scale: 1.1 },
  TempleMonolythLarge: { variants: ["exRockL2A", "exRockL3A"], scale: 2.0 },
  ZerusPlantClusterSmall: { variants: ["bushCommon"], scale: 0.4 },
  Aiur_CliffRocks:     { variants: ["exRockM2A", "exRockM3A"], scale: 1.4 },

  // — explicit skips (KTS logic / weather / lighting markers, not visible doodads) —
  // WoodenCrate / WoodenBarrel were originally mapped to medieval_village FBX
  // (Crate.fbx / Barrel.fbx) in the spec; deferred to a future pass since the
  // doodad render path is GLTF-only. They fall through to the per-shape bucket
  // fallback in world.ts (= doodadOther = mushroom). 21 instances, accepted.
  InvisibleCurtain:                "skip",
  PathingandSightBlocker1x1Doodad: "skip",
  PathingandSightBlocker2x2Doodad: "skip",
  PathingBlocker1x1Doodad:         "skip",
  PathingBlocker2x2Doodad:         "skip",
  WindowBoards:                    "skip",
  Waterfall:                       "skip",
  CrowdKobold:                     "skip",
  Candle1:                         "skip",
  Candle2:                         "skip",
  QancakesCandle:                  "skip",
  LightOmniWhite:                  "skip",
  FogMist:                         "skip",
  FogGrey:                         "skip",
  CloudShadows:                    "skip",
  LavaSplash:                      "skip",
  CharAshWeather:                  "skip",
  ZerusPoolSparkles:               "skip",
  ZerusLavaGroundCrack:            "skip",
  ColonistSupplies:                "skip",
  ColonistCorpse:                  "skip",
  ShakurasArchonStatue:            "skip",
  ChainLinkFencePost:              "skip",
};

// Stable hash for variant selection. ASCII sum is enough for ~3500 instances —
// no cryptographic guarantees needed, just stable across reloads.
export function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return h >>> 0;
}

export type DoodadResolution =
  // `tint` (when set) overrides `entry.tint`. Computed from `tintPool` per-instance
  // when the entry has one; otherwise falls back to `entry.tint`. Callers should
  // read `tint` for the final value rather than `entry.tint`.
  | { kind: "mesh"; variant: NatureKey; entry: DoodadEntry; tint?: number }
  | { kind: "skip" }
  | { kind: "miss" };  // not in registry — caller falls through to bucket

export function resolveDoodad(type: string, objectId: string): DoodadResolution {
  const row = DOODAD_REGISTRY[type];
  if (row === undefined) return { kind: "miss" };
  if (row === "skip") return { kind: "skip" };
  const variant = row.variants[hashId(objectId) % row.variants.length]!;
  let tint: number | undefined = row.tint;
  if (row.tintPool && row.tintPool.length > 0) {
    // Salted hash so variant + tint picks aren't correlated (otherwise every
    // commonTree3 would land on the same tint).
    const pick = row.tintPool[hashId(objectId + ":t") % row.tintPool.length]!;
    tint = pick === 0 ? undefined : pick;
  }
  return { kind: "mesh", variant, entry: row, tint };
}
